import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { useNotificationsStore } from './use-notifications-store'
import { useAuthStore } from './use-auth-store'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export type WishlistPriority = 'urgent' | 'desired' | 'later'

export interface WishlistItem {
  productId: string
  productName: string
  productImage: string
  price: number
  addedAt: number
  /** Optional note like "Хочу на день рождения" */
  note: string
  /** Priority badge */
  priority: WishlistPriority
  /** Toggle to notify when price drops (UI-only) */
  notifyPriceDrop: boolean
}

export type WishlistCategory = 'all' | 'urgent' | 'desired' | 'viewed'

interface RecentlyViewedItem {
  productId: string
  productName: string
  productImage: string
  price: number
  viewedAt: number
}

interface WishlistState {
  items: WishlistItem[]
  addItem: (item: Omit<WishlistItem, 'addedAt' | 'note' | 'priority' | 'notifyPriceDrop'>) => void
  removeItem: (productId: string) => void
  isWished: (productId: string) => boolean
  toggleItem: (item: Omit<WishlistItem, 'addedAt' | 'note' | 'priority' | 'notifyPriceDrop'>) => void
  clearWishlist: () => void
  totalCount: () => number
  /** Update a note for a wishlist item */
  updateNote: (productId: string, note: string) => void
  /** Update priority for a wishlist item */
  updatePriority: (productId: string, priority: WishlistPriority) => void
  /** Toggle price drop notification */
  togglePriceDropNotify: (productId: string) => void
  /** Sync wishlist (called on login) */
  syncOnLogin: () => void
}

interface RecentlyViewedState {
  items: RecentlyViewedItem[]
  addItem: (item: Omit<RecentlyViewedItem, 'viewedAt'>) => void
  clearRecent: () => void
}

export const useWishlistStore = create<WishlistState>()(
  persist(
    (set, get) => ({
      items: [],

      addItem: (item) => {
        set((state) => {
          if (state.items.some((i) => i.productId === item.productId)) return state
          return {
            items: [
              {
                ...item,
                addedAt: Date.now(),
                note: '',
                priority: 'desired' as WishlistPriority,
                notifyPriceDrop: false,
              },
              ...state.items,
            ],
          }
        })
        try {
          useNotificationsStore.getState().addNotification({
            type: 'success',
            title: 'Добавлено в избранное',
            message: item.productName,
          })
        } catch { /* ignore */ }

        // If logged in, dispatch notification about wishlist change
        try {
          const user = useAuthStore.getState().user
          if (user) {
            useNotificationsStore.getState().addNotification({
              type: 'info',
              title: 'Список желаний обновлён',
              message: `${item.productName} добавлен в ваш список`,
            })
          }
        } catch { /* ignore */ }
      },

      removeItem: (productId) => {
        const existing = get().items.find((i) => i.productId === productId)
        set((state) => ({
          items: state.items.filter((i) => i.productId !== productId),
        }))
        if (existing) {
          try {
            useNotificationsStore.getState().addNotification({
              type: 'info',
              title: 'Удалено из избранного',
              message: existing.productName,
            })
          } catch { /* ignore */ }
        }
      },

      isWished: (productId) => {
        return get().items.some((i) => i.productId === productId)
      },

      toggleItem: (item) => {
        if (get().isWished(item.productId)) {
          get().removeItem(item.productId)
        } else {
          get().addItem(item)
        }
      },

      clearWishlist: () => set({ items: [] }),

      totalCount: () => get().items.length,

      updateNote: (productId, note) => {
        set((state) => ({
          items: state.items.map((i) =>
            i.productId === productId ? { ...i, note } : i
          ),
        }))
      },

      updatePriority: (productId, priority) => {
        set((state) => ({
          items: state.items.map((i) =>
            i.productId === productId ? { ...i, priority } : i
          ),
        }))
        try {
          const item = get().items.find((i) => i.productId === productId)
          if (item) {
            const priorityLabels = {
              urgent: 'Срочно',
              desired: 'Желаемое',
              later: 'Может потом',
            }
            useNotificationsStore.getState().addNotification({
              type: 'info',
              title: 'Приоритет обновлён',
              message: `${item.productName} — ${priorityLabels[priority]}`,
            })
          }
        } catch { /* ignore */ }
      },

      togglePriceDropNotify: (productId) => {
        set((state) => ({
          items: state.items.map((i) =>
            i.productId === productId
              ? { ...i, notifyPriceDrop: !i.notifyPriceDrop }
              : i
          ),
        }))
        const item = get().items.find((i) => i.productId === productId)
        if (item) {
          try {
            useNotificationsStore.getState().addNotification({
              type: item.notifyPriceDrop ? 'success' : 'info',
              title: item.notifyPriceDrop
                ? 'Уведомление включено'
                : 'Уведомление отключено',
              message: item.notifyPriceDrop
                ? `Мы сообщим, если цена на "${item.productName}" снизится`
                : `Уведомление о снижении цены для "${item.productName}" отключено`,
            })
          } catch { /* ignore */ }
        }
      },

      syncOnLogin: () => {
        // Client-side sync: items already in localStorage via zustand/persist.
        // This is called on login to trigger UI feedback.
        const items = get().items
        try {
          useNotificationsStore.getState().addNotification({
            type: 'success',
            title: 'Список желаний синхронизирован',
            message: `Найдено ${items.length} ${items.length === 1 ? 'товар' : 'товаров'} в избранном`,
          })
        } catch { /* ignore */ }
      },
    }),
    {
      name: 'dveriant-wishlist',
    }
  )
)

export const useRecentlyViewedStore = create<RecentlyViewedState>()(
  persist(
    (set) => ({
      items: [],

      addItem: (item) => {
        set((state) => {
          const filtered = state.items.filter((i) => i.productId !== item.productId)
          return {
            items: [{ ...item, viewedAt: Date.now() }, ...filtered].slice(0, 12),
          }
        })
      },

      clearRecent: () => set({ items: [] }),
    }),
    {
      name: 'dveriant-recently-viewed',
    }
  )
)
